import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PROCESS } from "@/lib/site";
import { Reveal, SectionLabel } from "@/components/primitives";

export default function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 75%", "end 55%"] });
  const w = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const h = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section id="process" className="relative bg-[#F8FAFC] py-20 sm:py-28 lg:py-32" data-testid="process-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <SectionLabel color="#2563EB">Process</SectionLabel>
          <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-[#111827] sm:text-5xl lg:text-6xl">
            From idea to launch.
          </h2>
        </Reveal>

        <div ref={ref} className="relative mt-16">
          {/* desktop rail */}
          <div className="absolute left-0 right-0 top-8 hidden h-1 rounded-full bg-slate-200 lg:block">
            <motion.span className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#65A30D]" style={{ width: w }} />
          </div>
          {/* mobile rail */}
          <div className="absolute bottom-2 left-8 top-2 w-1 rounded-full bg-slate-200 lg:hidden">
            <motion.span className="absolute inset-x-0 top-0 rounded-full bg-gradient-to-b from-[#2563EB] via-[#7C3AED] to-[#65A30D]" style={{ height: h }} />
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-6">
            {PROCESS.map((p, i) => (
              <motion.div
                key={p.step}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="relative flex items-start gap-5 pl-0 lg:block"
                data-testid={`process-step-${p.step}`}
              >
                <div
                  className="relative z-10 grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-white font-display text-xl font-extrabold shadow-card ring-1 ring-slate-100"
                  style={{ color: p.color }}
                >
                  {p.step}
                </div>
                <div className="lg:mt-6">
                  <h3 className="font-display text-xl font-bold text-[#111827]">{p.name}</h3>
                  <p className="mt-1.5 max-w-[16rem] text-sm leading-relaxed text-slate-600">{p.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
